import { createSlice } from '@reduxjs/toolkit';
import { arrayToObject } from 'scenes/AreaPlanning/ImageCanvas/utils/arrayToObject';
import {
  closeLixaniPaintDialog,
  openLixaniPaintDialog,
} from '../actions/lixani-paint.actions';

const initialState = {
  lineWidth: 4,
  lineCap: 'round',
  lineColor: '#000000',
  fillColor: '#0000ff',
  fillOpacity: 0,
  fontSize: 16,
  fontFamily: 'Arial',
  textColor: '#000000',
  selectedTool: 'circle',
  context: 'lixaniPaint', // 'lixaniPaint' or 'areaPlanning'
  lixaniPaintFeature: '',
  createdAreas: {},
  createdTexts: {},
  createdLines: {},
  createdArrows: {},
  createdPenStrokes: {},
  createdSquares: {},
  createdCircles: {},
  createdLegends: {},
  createdIcons: {},
  selectedArea: null,
  selectedText: null,
  selectedLine: null,
  selectedArrow: null,
  selectedSquare: null,
  selectedCircle: null,
  selectedLegend: null,
  selectedIcon: null,
  selectedIconName: 'health',
  selectedIconSize: 1,
  selectedIconColor: '#000000',
  isCreatingLegendItem: false,
  pickedLegendColor: '#000000',
  pickedLegendText: '',
  pickedLegendTool: '',
};

export const drawingToolSlice = createSlice({
  name: 'drawingTool',
  initialState,
  reducers: {
    setContext: (state, action) => {
      state.context = action.payload;
      // Only set fillOpacity if it's still at default values
      if (
        action.payload === 'areaPlanning' &&
        (state.fillOpacity === 0 || state.fillOpacity === 0.2)
      ) {
        state.fillOpacity = 0.2;
        state.selectedTool = 'select';
      } else if (action.payload === 'lixaniPaint') {
        state.fillOpacity = 0;
        state.selectedTool = 'circle';
      }
    },
    setControlData: (state, action) => {
      Object.assign(state, action.payload);
    },
    setBackgroundImage: (state, action) => {
      state.backgroundImage = action.payload;
    },
    setSelectedTool: (state, action) => {
      state.selectedTool = action.payload;
    },
    createNewArea: (state, action) => {
      state.createdAreas = {
        ...state.createdAreas,
        [action.payload.id]: action.payload,
      };
    },
    createNewText: (state, action) => {
      state.createdTexts = {
        ...state.createdTexts,
        [action.payload.id]: action.payload,
      };
    },
    createNewLine: (state, action) => {
      state.createdLines = {
        ...state.createdLines,
        [action.payload.id]: action.payload,
      };
    },
    createNewArrow: (state, action) => {
      state.createdArrows = {
        ...state.createdArrows,
        [action.payload.id]: action.payload,
      };
    },
    createNewPenStroke: (state, action) => {
      state.createdPenStrokes = {
        ...state.createdPenStrokes,
        [action.payload.id]: action.payload,
      };
    },
    createNewSquare: (state, action) => {
      state.createdSquares = {
        ...state.createdSquares,
        [action.payload.id]: action.payload,
      };
    },
    createNewCircle: (state, action) => {
      state.createdCircles = {
        ...state.createdCircles,
        [action.payload.id]: action.payload,
      };
    },
    createNewLegend: (state, action) => {
      state.createdLegends = {
        ...state.createdLegends,
        [action.payload.id]: action.payload,
      };
    },
    createNewIcon: (state, action) => {
      state.createdIcons = {
        ...state.createdIcons,
        [action.payload.id]: action.payload,
      };
    },
    setSelectedArea: (state, action) => {
      state.selectedArea = action.payload;
    },
    setSelectedText: (state, action) => {
      state.selectedText = action.payload;
    },
    setSelectedLine: (state, action) => {
      state.selectedLine = action.payload;
    },
    setSelectedArrow: (state, action) => {
      state.selectedArrow = action.payload;
    },
    setSelectedSquare: (state, action) => {
      state.selectedSquare = action.payload;
    },
    setSelectedCircle: (state, action) => {
      state.selectedCircle = action.payload;
    },
    setSelectedLegend: (state, action) => {
      state.selectedLegend = action.payload;
    },
    setSelectedIcon: (state, action) => {
      state.selectedIcon = action.payload;
    },
    setSelectedIconName: (state, action) => {
      state.selectedIconName = action.payload;
    },
    setSelectedIconSize: (state, action) => {
      state.selectedIconSize = action.payload;
    },
    setSelectedIconColor: (state, action) => {
      state.selectedIconColor = action.payload;
    },
    updateArea: (state, action) => {
      state.createdAreas = {
        ...state.createdAreas,
        [action.payload.id]: action.payload,
      };
    },
    updateText: (state, action) => {
      state.createdTexts = {
        ...state.createdTexts,
        [action.payload.id]: action.payload,
      };
    },
    updateLine: (state, action) => {
      state.createdLines = {
        ...state.createdLines,
        [action.payload.id]: action.payload,
      };
    },
    updateArrow: (state, action) => {
      state.createdArrows = {
        ...state.createdArrows,
        [action.payload.id]: action.payload,
      };
    },
    updateSquare: (state, action) => {
      state.createdSquares = {
        ...state.createdSquares,
        [action.payload.id]: action.payload,
      };
    },
    updateCircle: (state, action) => {
      state.createdCircles = {
        ...state.createdCircles,
        [action.payload.id]: action.payload,
      };
    },
    updateLegend: (state, action) => {
      state.createdLegends = {
        ...state.createdLegends,
        [action.payload.id]: action.payload,
      };
      if (state.selectedLegend?.id === action.payload.id) {
        state.selectedLegend = action.payload;
      }
    },
    updateIcon: (state, action) => {
      state.createdIcons = {
        ...state.createdIcons,
        [action.payload.id]: action.payload,
      };
    },
    deleteText: (state, action) => {
      delete state.createdTexts[action.payload.id];
    },
    deleteLine: (state, action) => {
      delete state.createdLines[action.payload.id];
    },
    deleteArea: (state, action) => {
      delete state.createdAreas[action.payload.id];
    },
    deleteArrow: (state, action) => {
      delete state.createdArrows[action.payload.id];
    },
    deletePenStroke: (state, action) => {
      delete state.createdPenStrokes[action.payload.id];
    },
    deleteSquare: (state, action) => {
      delete state.createdSquares[action.payload.id];
    },
    deleteCircle: (state, action) => {
      delete state.createdCircles[action.payload.id];
    },
    deleteLegend: (state, action) => {
      delete state.createdLegends[action.payload.id];
    },
    deleteIcon: (state, action) => {
      delete state.createdIcons[action.payload.id];
    },
    loadDrawingToolControlData: (state, action) => {
      state.createdAreas = arrayToObject(action.payload.createdAreas);
      state.createdTexts = arrayToObject(action.payload.createdTexts);
      state.createdLines = arrayToObject(action.payload.createdLines);
      state.createdArrows = arrayToObject(action.payload.createdArrows);
      state.createdPenStrokes = arrayToObject(action.payload.createdPenStrokes);
      state.createdSquares = arrayToObject(action.payload.createdSquares || []);
      state.createdCircles = arrayToObject(action.payload.createdCircles || []);
      state.createdLegends = arrayToObject(action.payload.createdLegends || []);
      state.createdIcons = arrayToObject(action.payload.createdIcons || []);
    },

    resetControlData: (state, action) => {
      const { context, lixaniPaintFeature } = state;
      Object.assign(state, initialState);
      state.context = context;
      state.lixaniPaintFeature = lixaniPaintFeature;
      if (context === 'areaPlanning') {
        state.fillOpacity = 0.2;
        state.selectedTool = 'select';
      } else if (lixaniPaintFeature === 'firestop_documentation') {
        state.selectedTool = 'icon';
        state.selectedIconName = 'map-marker';
        state.selectedIconColor = '#ff0000';
        state.selectedIconSize = 2;
      }
    },

    setIsCreatingLegendItem: (state, action) => {
      state.isCreatingLegendItem = action.payload;
    },
    setPickedLegendColor: (state, action) => {
      state.pickedLegendColor = action.payload.color;
      state.pickedLegendText = action.payload.text;
      state.pickedLegendTool = action.payload.tool;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(openLixaniPaintDialog, (state, action) => {
        const activeFeature = action.payload;

        state.lixaniPaintFeature = activeFeature;
        state.fillOpacity = 0;

        state.context = 'lixaniPaint';

        if (activeFeature === 'firestop_documentation') {
          state.selectedTool = 'icon';
          state.selectedIconName = 'map-marker';
          state.selectedIconColor = '#ff0000';
          state.selectedIconSize = 2;
        } else {
          state.lineColor = '#ff0000';
          state.selectedTool = 'circle';
        }
      })
      .addCase(closeLixaniPaintDialog, (state, action) => {
        Object.assign(state, initialState);
      });
  },
});

// ACTIONS
export const {
  setBackgroundImage,
  setSelectedTool,
  setContext,
  setControlData,
  createNewArea,
  createNewText,
  createNewLine,
  createNewArrow,
  createNewPenStroke,
  createNewSquare,
  createNewCircle,
  createNewLegend,
  createNewIcon,
  setSelectedArea,
  setSelectedText,
  setSelectedLine,
  setSelectedArrow,
  setSelectedSquare,
  setSelectedCircle,
  setSelectedLegend,
  setSelectedIcon,
  setSelectedIconName,
  setSelectedIconSize,
  setSelectedIconColor,
  updateArea,
  updateLine,
  updateText,
  updateArrow,
  updateSquare,
  updateCircle,
  updateLegend,
  updateIcon,
  deleteLine,
  deleteText,
  deleteArea,
  deleteArrow,
  deletePenStroke,
  deleteSquare,
  deleteCircle,
  deleteLegend,
  deleteIcon,
  loadDrawingToolControlData,
  resetControlData,
  setIsCreatingLegendItem,
  setPickedLegendColor,
} = drawingToolSlice.actions;

// SELECTORS
export const getDrawingToolControlData = (state) => {
  const slice = state.drawingTool;

  return {
    ...slice,
  };
};

export const getSelectedTool = (state) => {
  return state.drawingTool.selectedTool;
};

export const getAreas = (state) => {
  return Object.values(state.drawingTool.createdAreas);
};

export const getTexts = (state) => {
  return Object.values(state.drawingTool.createdTexts);
};

export const getLines = (state) => {
  return Object.values(state.drawingTool.createdLines);
};

export const getArrows = (state) => {
  return Object.values(state.drawingTool.createdArrows);
};

export const getPenStrokes = (state) => {
  return Object.values(state.drawingTool.createdPenStrokes);
};

export const getSelectedArea = (state) => {
  return state.drawingTool.selectedArea;
};

export const getSelectedText = (state) => {
  return state.drawingTool.selectedText;
};

export const getSelectedLine = (state) => {
  return state.drawingTool.selectedLine;
};

export const getSelectedArrow = (state) => {
  return state.drawingTool.selectedArrow;
};

export const getSquares = (state) => {
  return Object.values(state.drawingTool.createdSquares);
};

export const getCircles = (state) => {
  return Object.values(state.drawingTool.createdCircles);
};

export const getSelectedSquare = (state) => {
  return state.drawingTool.selectedSquare;
};

export const getSelectedCircle = (state) => {
  return state.drawingTool.selectedCircle;
};

export const getLegends = (state) => {
  return Object.values(state.drawingTool.createdLegends);
};

export const getSelectedLegend = (state) => {
  return state.drawingTool.selectedLegend;
};

export const getIsCreatingLegendItem = (state) => {
  return state.drawingTool.isCreatingLegendItem;
};

export const getPickedLegendColor = (state) => {
  return state.drawingTool.pickedLegendColor;
};

export const getPickedLegendText = (state) => {
  return state.drawingTool.pickedLegendText;
};

export const getPickedLegendTool = (state) => {
  return state.drawingTool.pickedLegendTool;
};

export const getIcons = (state) => {
  return Object.values(state.drawingTool.createdIcons);
};

export const getSelectedIcon = (state) => {
  return state.drawingTool.selectedIcon;
};

export const getSelectedIconName = (state) => {
  return state.drawingTool.selectedIconName;
};

export const getSelectedIconSize = (state) => {
  return state.drawingTool.selectedIconSize;
};

export const getSelectedIconColor = (state) => {
  return state.drawingTool.selectedIconColor;
};
